/**
 * A procedural sky behind everything: shown where the scan's baked sky was removed ("Replace the
 * scan's sky") when the scene has no skybox of its own.
 *
 * A camera-centred sphere drawn first (Skybox layer, no depth write), textured with an equirect
 * gradient painted on a canvas: zenith to horizon colour with a horizon haze band, a soft glow
 * around the clouds' sun, and a hazy band below the horizon (the scan covers the ground). The
 * texture is repainted only when the preset or the sun moves.
 */
import * as pc from 'playcanvas';
import type { SkyBackdrop } from './scanSky';

export interface SkyPalette {
    zenith: [number, number, number];
    horizon: [number, number, number];
    below: [number, number, number];
    sun: [number, number, number];
    /** 0-1: how wide and strong the sun glow is. */
    glow: number;
}

/** sRGB 0..1 palettes for the backdrop presets. */
export const SKY_PALETTES: Record<Exclude<SkyBackdrop, 'none'>, SkyPalette> = {
    clear: { zenith: [0.24, 0.45, 0.78], horizon: [0.74, 0.84, 0.93], below: [0.62, 0.68, 0.74], sun: [1, 0.97, 0.9], glow: 0.5 },
    hazy: { zenith: [0.44, 0.6, 0.79], horizon: [0.86, 0.89, 0.91], below: [0.72, 0.74, 0.76], sun: [1, 0.96, 0.88], glow: 0.7 },
    golden: { zenith: [0.32, 0.45, 0.7], horizon: [0.98, 0.78, 0.55], below: [0.62, 0.52, 0.46], sun: [1, 0.86, 0.6], glow: 0.9 },
    overcast: { zenith: [0.6, 0.64, 0.69], horizon: [0.8, 0.82, 0.84], below: [0.66, 0.68, 0.7], sun: [0.95, 0.95, 0.95], glow: 0.15 },
};

const smooth = (a: number, b: number, x: number) => {
    const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
    return t * t * (3 - 2 * t);
};

/**
 * Paint an equirect sky (sRGB bytes, RGBA) laid out like pc.createSphere's UVs: rows from the
 * zenith down to the nadir, columns around the horizon. `sunDir` is the toward-sun unit vector in
 * world space.
 */
export function paintSky(width: number, height: number, palette: SkyPalette, sunDir: { x: number; y: number; z: number }): Uint8ClampedArray {
    const out = new Uint8ClampedArray(width * height * 4);
    const sl = Math.hypot(sunDir.x, sunDir.y, sunDir.z) || 1;
    const sx = sunDir.x / sl, sy = sunDir.y / sl, sz = sunDir.z / sl;
    for (let j = 0; j < height; j++) {
        const theta = ((j + 0.5) / height) * Math.PI; // 0 zenith .. pi nadir
        const y = Math.cos(theta);
        const ring = Math.sin(theta);
        for (let i = 0; i < width; i++) {
            // pc's sphere: u = 1 − lon, phi = 2π·lon − π/2, x = cos(phi)·ring, z = sin(phi)·ring
            const phi = 2 * Math.PI * (1 - (i + 0.5) / width) - Math.PI / 2;
            const x = Math.cos(phi) * ring;
            const z = Math.sin(phi) * ring;
            let c: number[];
            if (y >= 0) {
                const t = Math.pow(1 - y, 2.4); // 0 at zenith, 1 at the horizon
                c = [0, 1, 2].map((k) => palette.zenith[k] + (palette.horizon[k] - palette.zenith[k]) * t);
            } else {
                const t = smooth(0, 0.25, -y);
                c = [0, 1, 2].map((k) => palette.horizon[k] + (palette.below[k] - palette.horizon[k]) * t);
            }
            const mu = x * sx + y * sy + z * sz;
            const glow = palette.glow * (0.55 * Math.pow(Math.max(0, mu), 24) + 0.3 * Math.pow(Math.max(0, mu), 4)) * (sy > -0.05 ? 1 : 0);
            const o = (j * width + i) * 4;
            out[o] = Math.round(255 * Math.min(1, c[0] + palette.sun[0] * glow));
            out[o + 1] = Math.round(255 * Math.min(1, c[1] + palette.sun[1] * glow));
            out[o + 2] = Math.round(255 * Math.min(1, c[2] + palette.sun[2] * glow));
            out[o + 3] = 255;
        }
    }
    return out;
}

export class ProceduralSky {
    private readonly app: pc.AppBase;
    private readonly camera: pc.Entity;
    private entity: pc.Entity | null = null;
    private texture: pc.Texture | null = null;
    private material: pc.StandardMaterial | null = null;
    private key = '';
    private readonly lastPos = new pc.Vec3(Infinity, 0, 0);

    constructor(app: pc.AppBase, camera: pc.Entity) {
        this.app = app;
        this.camera = camera;
    }

    /** Show (or repaint) the backdrop; null hides it. */
    set(backdrop: SkyBackdrop | null, sunDir: pc.Vec3): void {
        if (!backdrop || backdrop === 'none') {
            if (this.entity?.enabled) this.entity.enabled = false;
            return;
        }
        const palette = SKY_PALETTES[backdrop];
        const key = `${backdrop}|${sunDir.x.toFixed(2)},${sunDir.y.toFixed(2)},${sunDir.z.toFixed(2)}`;
        if (!this.entity) this.create();
        if (key !== this.key) {
            this.key = key;
            const w = 512, h = 256;
            const pixels = paintSky(w, h, palette, sunDir);
            const tex = this.texture!;
            const data = tex.lock() as Uint8Array;
            data.set(pixels);
            tex.unlock();
        }
        if (!this.entity!.enabled) this.entity!.enabled = true;
        this.follow();
    }

    /** Keep the sphere on the camera (only when the camera moved, so idle frames stay idle). */
    follow(): void {
        if (!this.entity?.enabled) return;
        const p = this.camera.getPosition();
        if (p.equals(this.lastPos)) return;
        this.lastPos.copy(p);
        this.entity.setPosition(p);
    }

    private create(): void {
        const device = this.app.graphicsDevice;
        this.texture = new pc.Texture(device, {
            name: 'ProceduralSky',
            width: 512,
            height: 256,
            // sRGB bytes: sampled as linear and encoded back on output
            format: pc.PIXELFORMAT_SRGBA8,
            mipmaps: false,
            minFilter: pc.FILTER_LINEAR,
            magFilter: pc.FILTER_LINEAR,
            addressU: pc.ADDRESS_REPEAT,
            addressV: pc.ADDRESS_CLAMP_TO_EDGE,
        });
        const material = new pc.StandardMaterial();
        material.useLighting = false;
        material.useFog = false;
        material.useSkybox = false;
        material.diffuse.set(0, 0, 0);
        material.emissive.set(1, 1, 1);
        material.emissiveMap = this.texture;
        material.cull = pc.CULLFACE_FRONT;
        material.depthWrite = false;
        material.update();
        this.material = material;
        const far = Math.max(10, (this.camera.camera?.farClip ?? 1000) * 0.9);
        const entity = new pc.Entity('ProceduralSky');
        entity.addComponent('render', {
            type: 'sphere',
            material,
            castShadows: false,
            receiveShadows: false,
            layers: [pc.LAYERID_SKYBOX],
        });
        entity.setLocalScale(far * 2, far * 2, far * 2);
        this.app.root.addChild(entity);
        this.entity = entity;
    }

    destroy(): void {
        this.entity?.destroy();
        this.entity = null;
        this.material?.destroy();
        this.material = null;
        this.texture?.destroy();
        this.texture = null;
    }
}
